import React from 'react';
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EmptyReportSection from './EmptyReportSection';
import FlowListFailuresGroupedTable from './FlowListFailuresGroupedTable';
import { numberOrZero } from './aiReportUtils';
import {
  formatRepeatedFailuresGroupedTableValue,
  getRepeatedFailuresGroupedTableAttributes,
  getRepeatedFailuresGroupedTableFlows,
} from './repeatedFailuresGroupedTableUtils';
import { reportStyles } from './styles';

const getAttributeKey = (attribute, groupKey, index) =>
  `${groupKey}-${attribute.attribute || attribute.event || 'attribute'}-${index}`;

export default function AttributeListFailuresGroupedTable({ attributes = [], groupKey }) {
  const [expandedAttributesByKey, setExpandedAttributesByKey] = React.useState({});

  const handleAttributeExpandChange = (attributeKey) => {
    setExpandedAttributesByKey((prev) => ({
      ...prev,
      [attributeKey]: !prev[attributeKey],
    }));
  };

  if (attributes.length === 0) {
    return <EmptyReportSection>No repeated attributes reported.</EmptyReportSection>;
  }

  return (
    <TableContainer>
      <Table size="small" aria-label="Repeated failure attributes">
        <TableHead>
          <TableRow>
            <TableCell sx={reportStyles.tableHeaderCell} />
            <TableCell sx={reportStyles.tableHeaderCell}>Attribute</TableCell>
            <TableCell align="right" sx={reportStyles.tableHeaderCell}>
              Flow Count
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {attributes.map((attribute, index) => {
            const attributeKey = getAttributeKey(attribute, groupKey, index);
            const attributeFlows = getRepeatedFailuresGroupedTableFlows(attribute);
            const nestedAttributes = getRepeatedFailuresGroupedTableAttributes(attribute);
            const isExpanded = Boolean(expandedAttributesByKey[attributeKey]);

            return (
              <React.Fragment key={attributeKey}>
                <TableRow>
                  <TableCell sx={{ width: 48 }}>
                    <IconButton
                      aria-label={
                        isExpanded ? 'Collapse repeated attribute' : 'Expand repeated attribute'
                      }
                      size="small"
                      onClick={() => handleAttributeExpandChange(attributeKey)}
                    >
                      {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell sx={reportStyles.wordBreakCell}>
                    {formatRepeatedFailuresGroupedTableValue(attribute.attribute)}
                  </TableCell>
                  <TableCell align="right">{numberOrZero(attribute.flow_count)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={3} sx={{ py: 0, borderBottom: 0 }}>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 800, mb: 1 }}>
                          Attribute Flows
                        </Typography>
                        <FlowListFailuresGroupedTable
                          flows={attributeFlows}
                          emptyMessage="No flows reported."
                          showFailingAttributes={false}
                        />

                        {nestedAttributes.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 800, mb: 1 }}>
                              Nested Attributes
                            </Typography>
                            <TableContainer>
                              <Table size="small" aria-label="Nested repeated attributes">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={reportStyles.tableHeaderCell}>
                                      Attribute
                                    </TableCell>
                                    <TableCell sx={reportStyles.tableHeaderCell}>Event</TableCell>
                                    <TableCell align="right" sx={reportStyles.tableHeaderCell}>
                                      Flow Count
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {nestedAttributes.map((nestedAttribute, nestedIndex) => (
                                    <TableRow
                                      key={`${attributeKey}-${
                                        nestedAttribute.attribute ||
                                        nestedAttribute.event ||
                                        'nested-attribute'
                                      }-${nestedIndex}`}
                                    >
                                      <TableCell sx={reportStyles.wordBreakCell}>
                                        {formatRepeatedFailuresGroupedTableValue(
                                          nestedAttribute.attribute,
                                        )}
                                      </TableCell>
                                      <TableCell sx={reportStyles.wordBreakCell}>
                                        {formatRepeatedFailuresGroupedTableValue(
                                          nestedAttribute.event,
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        {numberOrZero(nestedAttribute.flow_count)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
